import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import Moment from "react-moment";
import {
  Button,
  Card,
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  CardHeader,
  CardBody,
  Badge,
  Tooltip,
} from "reactstrap";
import subscriptionsService from "../../services/api/subscription-membershipl-service";
import MembershipTransaction from "./MembershipTransaction";

const DetailedSubscription = () => {
  const { id } = useParams();
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copyUrl, setCopyUrl] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipOpenUrl, setTooltipOpenUrl] = useState(false);

  const toggleTooltip = () => setTooltipOpen(!tooltipOpen);
  const toggleTooltipUrl = () => setTooltipOpenUrl(!tooltipOpenUrl);

  const handleCopy = () => {
    navigator.clipboard.writeText(
      selectedSubscription?.pg_subscription_id || "-"
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(selectedSubscription?.share_url || "-");
    setCopyUrl(true);
    setTimeout(() => setCopyUrl(false), 2000);
  };

  const fetchSubscriptionDetails = async () => {
    setLoading(true);
    try {
      const response = await subscriptionsService.getDetails(id);
      if (response && response.status === true && response.data) {
        setSelectedSubscription(response.data);
      } else {
        setError("Failed to load membership details");
      }
    } catch (error) {
      console.error("Error fetching sales membership details:", error);
      setError("Error loading data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionDetails();
  }, [id]);

  const getStatusBadge = (status) => {
    if (!status) return <Badge color="secondary">Unknown</Badge>;

    switch (status.toLowerCase()) {
      case "created":
        return <Badge color="success">Created</Badge>;
      case "completed":
        return <Badge color="success">Completed</Badge>;
      case "activated":
        return <Badge color="success">Activated</Badge>;
      case "cancelled":
        return <Badge color="danger">Cancelled</Badge>;
      case "pending":
        return <Badge color="warning">Pending</Badge>;
      case "failed":
        return <Badge color="danger">Failed</Badge>;
      default:
        return <Badge color="info">{status}</Badge>;
    }
  };

  const formatDate = (timestamp) => {
    return timestamp
      ? moment.unix(timestamp).format("DD MMM YYYY, hh:mm A")
      : "-";
  };

  if (loading) {
    return (
      <div
        className="content d-flex justify-content-center align-items-center"
        style={{ height: "400px" }}
      >
        <Spinner color="primary" />
        <span className="ml-2">Loading membership details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content">
        <Container>
          <Alert color="danger">
            {error}
            <div className="mt-3">
              <Button color="primary" onClick={fetchSubscriptionDetails}>
                Try Again
              </Button>
              <Button
                className="ml-2"
                color="secondary"
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
            </div>
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="content">
      <Container fluid>
        <Card className="shadow-sm mb-4">
          <CardHeader>
            <div className="d-flex align-items-center justify-content-between">
              <div className="h4 mb-0">Membership Detailed View</div>
              <div>
                <Button
                  color="info"
                  size="sm"
                  className="mr-2"
                  onClick={fetchSubscriptionDetails}
                >
                  <i className="fa fa-refresh mr-1"></i> Refresh
                </Button>
                <Button
                  color="warning"
                  size="sm"
                  onClick={() => window.history.back()}
                >
                  <i className="fa fa-arrow-left mr-1"></i> Back
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {/* Header Summary Section */}
            <div className="bg-light py-2 px-3 mb-4 rounded border">
              <Row className="align-items-center">
                <Col md="4" className="mb-0">
                  <div className="d-flex align-items-center">
                    <h6 className="text-muted mb-0 me-2">ID:</h6>
                    <span className="font-weight-bold mb-0">
                      {selectedSubscription?.pg_subscription_id || "-"}
                    </span>
                    <button
                      className="btn btn-link me-1 p-1 "
                      onClick={handleCopy}
                      id="copyBtn"
                      style={{ color: copied ? "#28a745" : "#6c757d" }}
                    >
                      <i
                        className={`fa ${copied ? "fa-check" : "fa-copy"}`}
                        style={{ fontSize: "14px" }}
                      ></i>
                    </button>
                    <Tooltip
                      placement="top"
                      isOpen={tooltipOpen}
                      target="copyBtn"
                      toggle={toggleTooltip}
                    >
                      Copy ID
                    </Tooltip>
                  </div>
                </Col>
                <Col md="4" className="mb-0">
                  <div className="d-flex align-items-center">
                    <h6 className="text-muted mb-0 me-2">Project:</h6>
                    <span className="font-weight-bold pe-2">
                      {selectedSubscription?.project_id?.name || "-"}
                    </span>
                  </div>
                </Col>
                <Col md="4" className="mb-0">
                  <div className="d-flex align-items-center">
                    <h6 className="text-muted mb-0 me-2">Status:</h6>
                    <span className="font-weight-bold pe-2">
                      {getStatusBadge(selectedSubscription?.status)}
                    </span>
                  </div>
                </Col>
              </Row>
            </div>
            {/* Main Content - Two Column Layout */}
            <Row>
              {/* Left Column */}
              <Col md="6">
                <h6 className="mb-3">Membership Information</h6>
                <div className="border rounded mb-4">
                  <Row className="g-0">
                    <Col xs="5" className="p-2 bg-light">
                      <div className="fw-semibold text-dark">Project</div>
                    </Col>
                    <Col className="p-2">
                      <div className="fw-semibold">
                        {selectedSubscription?.project_id?.name || "-"}
                      </div>
                    </Col>
                  </Row>
                  <Row className="g-0 border-top">
                    <Col xs="5" className="p-2 bg-light">
                      <div className="fw-semibold text-dark">Paid Count</div>
                    </Col>
                    <Col className="p-2">
                      <div className="fw-semibold">
                        {selectedSubscription?.paid_count || "-"}
                      </div>
                    </Col>
                  </Row>

                  <Row className="g-0 border-top">
                    <Col xs="5" className="p-2 bg-light">
                      <div className="fw-semibold text-dark">Type</div>
                    </Col>
                    <Col className="p-2">
                      <div className="fw-semibold">
                        {selectedSubscription?.type
                          ? selectedSubscription.type === "onetime"
                            ? "One Time"
                            : "Recurring"
                          : selectedSubscription?.total_count === 1
                          ? "One Time"
                          : selectedSubscription?.total_count
                          ? "Recurring"
                          : "N/A"}
                      </div>
                    </Col>
                  </Row>
                  <Row className="g-0 border-top">
                    <Col xs="5" className="p-2 bg-light">
                      <div className="fw-semibold text-dark">Total Count</div>
                    </Col>
                    <Col className="p-2">
                      <div className="fw-semibold">
                        {selectedSubscription?.total_count || "-"}
                      </div>
                    </Col>
                  </Row>
                  <Row className="g-0 border-top">
                    <Col xs="5" className="p-2 bg-light">
                      <div className="fw-semibold text-dark">Share URL</div>
                    </Col>
                    <Col className="ps-2">
                      <div className="fw-semibold text-truncate d-flex align-items-center gap-2">
                        {selectedSubscription?.share_url ? (
                          <>
                            <Button
                              color="link"
                              className="p-0"
                              onClick={() =>
                                window.open(
                                  selectedSubscription.share_url,
                                  "_blank"
                                )
                              }
                            >
                              View URL
                            </Button>
                            <button
                              className="btn btn-link p-1"
                              onClick={handleCopyUrl}
                              id="copyBtnUrl"
                              style={{ color: copyUrl ? "#28a745" : "#6c757d" }}
                            >
                              <i
                                className={`fa ${
                                  copyUrl ? "fa-check" : "fa-copy"
                                }`}
                                style={{ fontSize: "14px" }}
                              ></i>
                            </button>
                            <Tooltip
                              placement="top"
                              isOpen={tooltipOpenUrl}
                              target="copyBtnUrl"
                              toggle={toggleTooltipUrl}
                            >
                              Copy URL
                            </Tooltip>
                          </>
                        ) : (
                          "-"
                        )}
                      </div>
                    </Col>
                  </Row>

                  <Row className="g-0 border-top">
                    <Col xs="5" className="p-2 bg-light">
                      <div className="fw-semibold text-dark">Platform</div>
                    </Col>
                    <Col className="p-2">
                      <div className="fw-semibold">
                        {selectedSubscription?.platform || "-"}
                      </div>
                    </Col>
                  </Row>
                </div>

                <h6 className="mb-3">Payment Details</h6>
                <div className="border rounded">
                  <Row className="g-0">
                    <Col xs="5" className="p-2 bg-light">
                      <div className="fw-semibold text-dark">
                        Payment Gateway
                      </div>
                    </Col>
                    <Col className="p-2">
                      <div className="fw-semibold">
                        {selectedSubscription?.payment_gateway || "-"}
                      </div>
                    </Col>
                  </Row>
                  <Row className="g-0 border-top">
                    <Col xs="5" className="p-2 bg-light">
                      <div className="fw-semibold text-dark">PG Plan ID</div>
                    </Col>
                    <Col className="p-2">
                      <div className="fw-semibold">
                        {selectedSubscription?.pg_plan_id || "-"}
                      </div>
                    </Col>
                  </Row>
                  <Row className="g-0 border-top">
                    <Col xs="5" className="p-2 bg-light">
                      <div className="fw-semibold text-dark">Plan ID</div>
                    </Col>
                    <Col className="p-2">
                      <div className="fw-semibold">
                        {selectedSubscription?.plan_id || "-"}
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>

              {/* Right Column */}
              <Col md="6">
                <h6 className="mb-3">Subscription Timeline</h6>
                <div className="border rounded mb-4">
                  <Row className="g-0">
                    <Col xs="5" className="p-2 bg-light">
                      <div className="fw-semibold text-dark">Created At</div>
                    </Col>
                    <Col className="p-2">
                      <div className="fw-semibold">
                        <Moment format="DD MMM YYYY, hh:mm A">
                          {selectedSubscription?.createdAt || "-"}
                        </Moment>
                      </div>
                    </Col>
                  </Row>
                  <Row className="g-0 border-top">
                    <Col xs="5" className="p-2 bg-light">
                      <div className="fw-semibold text-dark">Updated At</div>
                    </Col>
                    <Col className="p-2">
                      <div className="fw-semibold">
                        <Moment format="DD MMM YYYY, hh:mm A">
                          {selectedSubscription?.updatedAt || "-"}
                        </Moment>
                      </div>
                    </Col>
                  </Row>
                  <Row className="g-0 border-top">
                    <Col xs="5" className="p-2 bg-light">
                      <div className="fw-semibold text-dark">
                        Membership Start Date
                      </div>
                    </Col>
                    <Col className="p-2">
                      <div className="fw-semibold">
                        {formatDate(selectedSubscription?.start_at)}
                      </div>
                    </Col>
                  </Row>
                  <Row className="g-0 border-top">
                    <Col xs="5" className="p-2 bg-light">
                      <div className="fw-semibold text-dark">
                        Membership End Date
                      </div>
                    </Col>
                    <Col className="p-2">
                      <div className="fw-semibold">
                        {formatDate(selectedSubscription?.end_at)}
                      </div>
                    </Col>
                  </Row>
                  <Row className="g-0 border-top">
                    <Col xs="5" className="p-2 bg-light">
                      <div className="fw-semibold text-dark">
                        Current Period Start
                      </div>
                    </Col>
                    <Col className="p-2">
                      <div className="fw-semibold">
                        {formatDate(selectedSubscription?.current_start)}
                      </div>
                    </Col>
                  </Row>
                  <Row className="g-0 border-top">
                    <Col xs="5" className="p-2 bg-light">
                      <div className="fw-semibold text-dark">
                        Current Period End
                      </div>
                    </Col>
                    <Col className="p-2">
                      <div className="fw-semibold">
                        {formatDate(selectedSubscription?.current_end)}
                      </div>
                    </Col>
                  </Row>
                  {selectedSubscription?.status === "cancelled" && (
                    <Row className="g-0 border-top">
                      <Col xs="5" className="p-2 bg-light">
                        <div className="fw-semibold text-dark">
                          Cancelled At
                        </div>
                      </Col>
                      <Col className="p-2">
                        <div className="fw-semibold">
                          <Moment format="DD MMM YYYY, hh:mm A">
                            {selectedSubscription?.cancel_requested_at || "-"}
                          </Moment>
                        </div>
                      </Col>
                    </Row>
                  )}
                </div>

                {/* User Information */}
                <h6 className="mb-3">User Information</h6>
                <div className="border rounded">
                  <Row className="g-0">
                    <Col xs="5" className="p-2 bg-light">
                      <div className="fw-semibold text-dark">Name</div>
                    </Col>
                    <Col className="p-2">
                      <div className="fw-semibold">
                        {selectedSubscription?.user_id?.name || "-"}
                      </div>
                    </Col>
                  </Row>
                  <Row className="g-0 border-top">
                    <Col xs="5" className="p-2 bg-light">
                      <div className="fw-semibold text-dark">Email</div>
                    </Col>
                    <Col className="p-2">
                      <div className="fw-semibold">
                        {selectedSubscription?.user_id?.email || "-"}
                      </div>
                    </Col>
                  </Row>
                  <Row className="g-0 border-top">
                    <Col xs="5" className="p-2 bg-light">
                      <div className="fw-semibold text-dark">Phone No.</div>
                    </Col>
                    <Col className="p-2">
                      <div className="fw-semibold">
                        {selectedSubscription?.user_id?.phone_number || "-"}
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>

              <Col md="12">
                <h6 className="mt-4">Transactions</h6>

                {selectedSubscription?.project_id?._id &&
                  selectedSubscription?.user_id?._id &&
                  selectedSubscription?.pg_subscription_id && (
                    <div className="border rounded mt-4 md-4">
                      <MembershipTransaction
                        projectId={selectedSubscription.project_id._id}
                        user_id={selectedSubscription.user_id._id}
                        pg_subscription_id={
                          selectedSubscription.pg_subscription_id
                        }
                      />
                    </div>
                  )}
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default DetailedSubscription;
